import React, { useCallback, useMemo } from 'react';
import LayoutPage from 'components/Layout';
import { Switch } from 'react-router-dom';
import ProtectedRoute from 'components/ProtectedRoute';
import LoadingPage from 'components/LoadingPage';
import AddUser from './AddUser';
import Users from './Users';
import { Logout, UserSettings } from '@carbon/icons-react';
import { Redirect, useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectRoles } from 'features/auth';
import { ROLES } from 'constants';
import { getInternalMenuItems } from 'containers/Internal/Internal';
import { Button } from 'antd';
import MyAccount from 'containers/MyAccount/MyAccount';
import { FAMILY_ROLES_WITH_ACCESS_TO_USERS_PAGE, PROVIDER_ROLES_WITH_ACCESS_TO_USERS_PAGE } from './Users/Users';

const rootBreadcrumb = { text: 'Admin', to: '/admin' };
const addUserRegex = /^\/admin\/add-user(.*)$/;
const adminReviewAppRegex = /^\/admin\/review\/application(.*)$/;
const breadcrumbs = [
  [/\/admin\/?(users|providers|families|organizations)?$/, [rootBreadcrumb]],
  ['/admin/profile', [rootBreadcrumb, { text: 'Profile', to: 'admin/profile' }]],
  [addUserRegex, [rootBreadcrumb, { text: 'Add User', to: 'admin/add-user' }]],
  [/^\/admin\/update-user\/(.*)$/, [rootBreadcrumb, { text: 'Update User', to: 'admin/update-user' }]],
  ['/admin/messages', [rootBreadcrumb, { text: 'Messages', to: 'admin/messages' }]],
  [adminReviewAppRegex, [rootBreadcrumb, { text: 'Review', to: 'admin/review/application' }]],
];
export default function Admin() {
  const history = useHistory();
  const dispatch = useDispatch();
  const roles = useSelector(selectRoles);

  const menuOptions = useMemo(() => {
    const _options = getAdminMenuOptions(roles);

    if (
      [
        ROLES.PROVIDER_COORDINATOR,
        ROLES.PROVIDER_MANAGER,
        ROLES.FAMILY_COORDINATOR,
        ROLES.FAMILY_MANAGER,
        ROLES.PARENT_SERVICES_SPECIALIST,
        ROLES.PROVIDER_SUPPORT_SPECIALIST,
      ].some((_role) => roles.includes(_role))
    ) {
      return [...getInternalMenuItems({ history, roles }), ..._options];
    }
    return _options;
  }, [history, roles]);

  const _logout = useCallback(async () => {
    dispatch(logout.pending());
  }, [dispatch]);

  return (
    <LayoutPage
      headerIcons={
        <Button
          type="text"
          icon={<Logout />}
          style={{
            color: '#FFFFFF',
            textDecoration: 'underline',
          }}
          onClick={_logout}
        >
          Logout
        </Button>
      }
      menuOptions={menuOptions}
      breadcrumbs={breadcrumbs}
    >
      <React.Suspense fallback={<LoadingPage />}>
        <Switch>
          <ProtectedRoute path="/account" message="Navigated to My account">
            <MyAccount />
          </ProtectedRoute>

          {/* Order is important */}
          <ProtectedRoute exact path="/admin/profile" message="Navigated to Admin Dashboard">
            <AddUser key="profile" self />
          </ProtectedRoute>
          <ProtectedRoute exact path="/admin/add-user" message="Navigated to Admin Dashboard">
            <AddUser key="add-user" />
          </ProtectedRoute>

          <ProtectedRoute
            exact
            path={['/admin/users', '/admin/users/internal', '/admin/users/providers', '/admin/users/families']}
            message="Navigated to Admin Dashboard"
          >
            <Users key="users" />
          </ProtectedRoute>

          <ProtectedRoute exact path="/admin/update-user/:email" message="Navigated to Admin Dashboard">
            <AddUser />
          </ProtectedRoute>
          <ProtectedRoute exact path="/admin/:tab?" message="Navigated to Admin Dashboard">
            <Redirect to="/admin/users" />
          </ProtectedRoute>

          <ProtectedRoute message="No matching route.">
            <h3>No Content yet </h3>
          </ProtectedRoute>
        </Switch>
      </React.Suspense>
    </LayoutPage>
  );
}

export function getAdminMenuOptions(roles) {
  return roles.find(
    (role) =>
      FAMILY_ROLES_WITH_ACCESS_TO_USERS_PAGE.includes(role) || PROVIDER_ROLES_WITH_ACCESS_TO_USERS_PAGE.includes(role),
  )
    ? []
    : [
        {
          label: 'Users',
          icon: UserSettings,
          to: '/admin/users',
          match: [
            '/admin/users',
            '/admin/users/internal',
            '/admin/users/providers',
            '/admin/users/families',
            /^\/admin\/add-user(.*)$/,
            /^\/admin\/update-user\/(.*)$/,
            /^\/families\/admin\/[0-9]+\/.+/,
          ],
        },
      ];
}
